import { declaredSourceOf } from '@acs/shared';
import type { BusinessDoc } from '../api/converters.js';

/**
 * Which phone number to show the owner for a business — and whether it is
 * actually theirs.
 *
 * The distinction matters more than it sounds. `business.phoneNumber` is OUR
 * number: the forwarding target the owner points their line at. Under the
 * shared-number model (doc 18) it may be the same number as twenty other
 * businesses', and it is published nowhere. Showing it under a business name is
 * actively harmful — it reads as "this is your new business number", which
 * invites an owner to put it on a shopfront, a website or a Google listing. If
 * they ever leave, or we retire that number, everything they printed goes dead;
 * and while they stay, their customers are dialling a number that identifies
 * nobody.
 *
 * So: prefer the line the owner told us is theirs. Fall back to ours only
 * before they have declared one, where "No number yet" would be the only
 * alternative.
 */
export interface DisplayNumber {
  number: string | null;
  /** True when this is the owner's own line, false when it is our plumbing. */
  isOwn: boolean;
}

export function businessDisplayNumber(business: BusinessDoc): DisplayNumber {
  // `pendingSourceNumber` is the same fact before a number was provisioned:
  // setup asks for the owner's line first, so for a while their declaration is
  // the only number we hold for them. Showing ours instead would be exactly the
  // "here is your new business number" misread this function exists to avoid.
  const own = declaredSourceNumber(business);
  if (own) return { number: own, isOwn: true };
  return { number: business.phoneNumber ?? null, isOwn: false };
}

/**
 * The owner's declared line, wherever it currently lives.
 *
 * A `BusinessDoc`-typed alias for `declaredSourceOf` in `@acs/shared`, which is
 * where the rule itself lives — the server applies the same one when it decides
 * whether a declaration is new or a change.
 */
export function declaredSourceNumber(business: BusinessDoc): string | null {
  return declaredSourceOf(business);
}

/**
 * Can this business actually receive calls? — the honest version of "is the
 * number step done" (doc 18 §7.3).
 *
 * The answer depends on HOW it is routed, and the two cases are genuinely
 * different rather than one being a stricter version of the other:
 *
 * `dedicated` — it holds the number to itself and the gateway routes on the
 * number dialled. It is reachable the moment the number exists, with or without
 * a verification call. This is every business provisioned before doc 18, and
 * requiring a proof they were never asked for would retroactively mark working
 * businesses as broken.
 *
 * `pooled` — the number is shared and resolves ONLY through a routing grant, so
 * an unproven line means every caller is unattributable. Here verification is
 * the difference between answering and silently not, which is why doc 18
 * reverses doc 13a's "verification is advisory" rule for this case alone.
 */
export function isReachable(business: BusinessDoc): boolean {
  const fwd = business.forwardNumber;
  if (!fwd?.phoneNumber) return false;
  if (!isOnSharedNumber(business)) return true;
  // A PROVEN line, and nothing else. `poolingUnsupportedAt` used to count here
  // too, on the premise that such a business would be moved to a number of its
  // own — but numbers are no longer bought for anyone, so that business stays
  // on the shared number with no routing grant and every call to it is
  // unattributable. Counting it as reachable told its owner "setup complete"
  // about a line that answers nobody.
  return fwd.sourceNumber != null && fwd.sourceVerifiedAt != null;
}

/**
 * Is this business on the SHARED number (doc 18), where only a proven divert
 * makes it reachable?
 *
 * Not `mode === 'pooled'` alone. The gateway used to flip `mode` to
 * `'dedicated'` when a verification call could not be matched, while the
 * number underneath stayed the shared one — so records written before that was
 * fixed carry `mode: 'dedicated'` on a pool number, and `mode` alone would
 * wave them through as if they held a line of their own.
 * `poolingUnsupportedAt` is only ever set on a pooled business, so either
 * signal means "shared".
 */
export function isOnSharedNumber(business: BusinessDoc): boolean {
  const fwd = business.forwardNumber;
  return fwd != null && (fwd.mode === 'pooled' || fwd.poolingUnsupportedAt != null);
}

/**
 * Should the forwarding target still be on screen?
 *
 * Only while the owner is actively setting the divert up. Once a forwarded call
 * has proved their line, our number has done its job and showing it again is
 * just an invitation to confuse it with theirs (doc 18 §7.4). The way back is
 * changing the declared business number, which restarts the flow.
 */
export function shouldShowForwardingTarget(business: BusinessDoc): boolean {
  return business.forwardNumber != null && !isForwardingProven(business);
}

/**
 * Has a forwarded call proved the owner's line?
 *
 * Only `sourceVerifiedAt` counts. A call that arrived without a usable
 * diverted-from (`poolingUnsupportedAt`), or from a line other than the one
 * declared (`sourceMismatchAt`), reached us but proved nothing we can route
 * on — and since there is no longer a dedicated number to fall back to, the
 * check is NOT finished and the forwarding target stays on screen.
 *
 * False when no number is held yet — nothing has been proved about a divert
 * that has nowhere to point.
 */
export function isForwardingProven(business: BusinessDoc): boolean {
  const fwd = business.forwardNumber;
  return fwd != null && fwd.sourceVerifiedAt != null;
}

/**
 * The last verification call came from a DIFFERENT line than the one the owner
 * declared — almost always a typo in the declaration, occasionally a divert set
 * up on another SIM. Fixable by the owner, which is what separates it from
 * {@link isCarrierUnsupported}: correcting the number clears it.
 */
export function hasSourceMismatch(business: BusinessDoc): boolean {
  const fwd = business.forwardNumber;
  return fwd != null && fwd.sourceVerifiedAt == null && fwd.sourceMismatchAt != null;
}

/**
 * The carrier delivered the forwarded call but not the number it was diverted
 * from, so the shared number cannot tell this business's calls from anyone
 * else's (doc 18 D3). Not the owner's doing and not fixable from the portal —
 * and, with no numbers bought for anyone, not something we can serve today.
 */
export function isCarrierUnsupported(business: BusinessDoc): boolean {
  const fwd = business.forwardNumber;
  return fwd != null && fwd.sourceVerifiedAt == null && fwd.poolingUnsupportedAt != null;
}
